import { useCallback, useState } from 'react';
import { getCompilerSurveyInvitation } from 'services/api/compilerSurveyApi';
import { CompilerSurveyTrigger } from 'types/types';

export type CompilerSurveyInvitationState = {
  id: number;
  trigger: CompilerSurveyTrigger;
  surveyUrl: string;
};

export type UseCompilerSurveyOfferResult = {
  surveyInvitation: CompilerSurveyInvitationState | null;
  isSurveyVisible: boolean;
  offerCompilerSurvey: (trigger: CompilerSurveyTrigger) => void;
  closeSurvey: () => void;
};

/**
 * Asks the backend whether to invite the current user to the EPD Compiler survey after a successful trigger.
 * `offerCompilerSurvey` is fire-and-forget, so a slow or failing request does not affect the trigger handler.
 * Closing only hides the invitation: the dialog stays mounted so its hide transition releases the body scroll lock.
 */
const useCompilerSurveyOffer = (epdId: string): UseCompilerSurveyOfferResult => {
  const [surveyInvitation, setSurveyInvitation] = useState<CompilerSurveyInvitationState | null>(null);
  const [isSurveyVisible, setIsSurveyVisible] = useState<boolean>(false);

  const offerCompilerSurvey = useCallback(
    (trigger: CompilerSurveyTrigger): void => {
      getCompilerSurveyInvitation(epdId, trigger)
        .then((invitation) => {
          const surveyUrl = invitation?.show ? invitation.surveyUrl : undefined;
          if (surveyUrl) {
            setSurveyInvitation((previous) => ({ id: (previous?.id ?? 0) + 1, trigger, surveyUrl }));
            setIsSurveyVisible(true);
          }
        })
        .catch(() => {});
    },
    [epdId]
  );

  const closeSurvey = useCallback(() => setIsSurveyVisible(false), []);

  return { surveyInvitation, isSurveyVisible, offerCompilerSurvey, closeSurvey };
};

export default useCompilerSurveyOffer;
